import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, Copy, Mail } from 'lucide-react';
import { api } from '../../lib/api';
import { useSupportConfig } from '../../lib/useSupportConfig';
import { recentAssistantQuestions } from '../../lib/supportContext';

// Capa 3 del soporte escalonado (GOL-11 / D8): escalar a una persona.
//
// Regla que gobierna toda la pantalla: el cliente nunca puede quedarse sin canal.
// Si el envío falla o ya mandó demasiados mensajes hoy, la dirección de soporte
// queda a la vista con copiar y abrir correo — un formulario roto no puede ser
// el final del camino de alguien que necesita ayuda.

export default function SupportContactForm({ onBack }: { onBack: () => void }) {
  const location = useLocation();
  const support = useSupportConfig();
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<{ promise: string; replyTo: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function submit() {
    if (message.trim().length < 10 || sending) return;
    setSending(true);
    setError(null);
    try {
      const result = await api.support.contact({
        message: message.trim(),
        path: `${location.pathname}${location.search}`,
        askedBefore: recentAssistantQuestions(),
      });
      setSent({ promise: result.promise, replyTo: result.replyTo });
    } catch (e) {
      setError((e as Error).message || 'No pudimos enviar tu mensaje desde aquí.');
    } finally {
      setSending(false);
    }
  }

  async function copyEmail() {
    if (!support?.email) return;
    try {
      await navigator.clipboard.writeText(support.email);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('No pudimos copiarlo automáticamente. Selecciona la dirección y cópiala a mano.');
    }
  }

  if (sent) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.18 }}
        className="space-y-4"
      >
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-ui-success/15 text-ui-success">
            <Check size={16} />
          </span>
          <h2 className="text-base font-semibold text-ui-text">Listo, ya lo recibimos</h2>
        </div>
        <p className="text-sm leading-relaxed text-ui-text-muted">
          {sent.replyTo
            ? `Te contestamos a ${sent.replyTo}. ${sent.promise}`
            : sent.promise}
        </p>
        <p className="text-sm text-ui-text-subtle">No hace falta que vuelvas a escribir.</p>
        <button
          type="button"
          onClick={onBack}
          className="rounded-md border border-ui-border px-3 py-2 text-sm font-medium text-ui-text transition-colors hover:border-ui-accent"
        >
          Volver a la ayuda
        </button>
      </motion.div>
    );
  }

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm text-ui-text-muted transition-colors hover:text-ui-text"
      >
        <ArrowLeft size={14} />
        Volver
      </button>

      <div className="space-y-1">
        <h2 className="text-base font-semibold text-ui-text">Escríbele a una persona</h2>
        <p className="text-sm text-ui-text-muted">{support?.promise ?? 'Te contestamos lo antes posible.'}</p>
      </div>

      <div className="space-y-1.5">
        <label htmlFor="support-message" className="block text-sm text-ui-text-muted">
          ¿Qué está pasando?
        </label>
        <textarea
          id="support-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={6}
          placeholder="Cuéntanos con tus palabras qué intentabas hacer y qué pasó. Entre más detalle, más rápido te resolvemos."
          className="w-full resize-y rounded-md border border-ui-border bg-ui-bg px-3 py-2 text-sm text-ui-text placeholder:text-ui-text-subtle focus:border-ui-accent focus:outline-none"
        />
      </div>

      <p className="text-sm text-ui-text-subtle">
        Junto con tu mensaje le compartimos a soporte tu correo, el nombre de tu negocio y en qué
        pantalla estabas.
      </p>

      {error && (
        <div className="space-y-2 rounded-lg border border-ui-warning/40 bg-ui-warning/10 px-3 py-2.5">
          <p className="text-sm text-ui-text">{error}</p>
          {support?.email && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={copyEmail}
                className="inline-flex items-center gap-1.5 rounded-md border border-ui-border px-2.5 py-1.5 text-sm text-ui-text transition-colors hover:border-ui-accent"
              >
                <Copy size={14} />
                {copied ? 'Copiada' : 'Copiar dirección'}
              </button>
              <a
                href={`mailto:${support.email}`}
                className="inline-flex items-center gap-1.5 rounded-md border border-ui-border px-2.5 py-1.5 text-sm text-ui-text transition-colors hover:border-ui-accent"
              >
                <Mail size={14} />
                Abrir mi correo
              </a>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={submit}
          disabled={sending || message.trim().length < 10}
          className="rounded-md bg-ui-accent px-4 py-2 text-sm font-medium text-ui-text-inverse transition-colors hover:bg-ui-accent-hover disabled:opacity-50"
        >
          {sending ? 'Enviando…' : 'Enviar mensaje'}
        </button>
        {support?.email && (
          <span className="text-sm text-ui-text-subtle">
            o escríbenos a <span className="text-ui-text-muted">{support.email}</span>
          </span>
        )}
      </div>
    </div>
  );
}
